import { useEffect, useRef, useState } from 'react';
import { useMap } from 'react-leaflet';
import './LayerSwipe.css';

interface LayerSwipeProps {
  enabled: boolean;
  sliderPosition: number;
  onSliderChange: (position: number) => void;
  leftLayerId?: string;
  rightLayerId?: string;
}

const LayerSwipe = ({ enabled, sliderPosition, onSliderChange, leftLayerId, rightLayerId }: LayerSwipeProps) => {
  const map = useMap();
  const sliderRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const [sliderLeftPx, setSliderLeftPx] = useState(0);

  useEffect(() => {
    if (!enabled || !map) return;

    const mapContainer = map.getContainer();

    // Create custom panes for swipe layers if they don't exist
    if (!map.getPane('swipe-left')) {
      map.createPane('swipe-left');
      const leftPane = map.getPane('swipe-left')!;
      leftPane.style.zIndex = '450'; // Above tile layers (400) but below overlays (600)
      leftPane.style.pointerEvents = 'none';
    }

    if (!map.getPane('swipe-right')) {
      map.createPane('swipe-right');
      const rightPane = map.getPane('swipe-right')!;
      rightPane.style.zIndex = '451'; // Just above left pane
      rightPane.style.pointerEvents = 'none';
    }

    // Apply clip to the custom panes
    const updateClip = () => {
      const mapWidth = mapContainer.offsetWidth;
      const mapHeight = mapContainer.offsetHeight;
      const clipX = (sliderPosition / 100) * mapWidth;

      // Update slider position in pixels to match clip exactly
      // Account for the 4px width of the slider to center it on the clip line
      setSliderLeftPx(clipX);

      // Clip the left pane (show only left side)
      const leftPane = map.getPane('swipe-left');
      if (leftPane) {
        leftPane.style.clip = `rect(0px, ${clipX}px, ${mapHeight}px, 0px)`;
      }

      // Clip the right pane (show only right side)
      const rightPane = map.getPane('swipe-right');
      if (rightPane) {
        rightPane.style.clip = `rect(0px, ${mapWidth}px, ${mapHeight}px, ${clipX}px)`;
      }
    };

    updateClip();

    // Update on map resize and move
    const resizeObserver = new ResizeObserver(updateClip);
    resizeObserver.observe(mapContainer);

    map.on('move', updateClip);
    map.on('zoom', updateClip);

    return () => {
      // Clean up clips
      const leftPane = map.getPane('swipe-left');
      const rightPane = map.getPane('swipe-right');

      if (leftPane) leftPane.style.clip = '';
      if (rightPane) rightPane.style.clip = '';

      resizeObserver.disconnect();
      map.off('move', updateClip);
      map.off('zoom', updateClip);
    };
  }, [enabled, sliderPosition, map, leftLayerId, rightLayerId]);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    isDraggingRef.current = true;

    // Disable map dragging
    if (map) {
      map.dragging.disable();
    }
  };

  const handleMouseMove = (e: MouseEvent) => {
    if (!isDraggingRef.current || !map) return;

    e.preventDefault();
    e.stopPropagation();

    const mapContainer = map.getContainer();
    const rect = mapContainer.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    onSliderChange(percentage);
  };

  const handleMouseUp = () => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;

    // Re-enable map dragging
    if (map) {
      map.dragging.enable();
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    e.stopPropagation();
    isDraggingRef.current = true;

    // Disable map dragging
    if (map) {
      map.dragging.disable();
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (!isDraggingRef.current || !map) return;

    e.preventDefault();
    e.stopPropagation();

    const touch = e.touches[0];
    const mapContainer = map.getContainer();
    const rect = mapContainer.getBoundingClientRect();
    const x = touch.clientX - rect.left;
    const percentage = Math.max(0, Math.min(100, (x / rect.width) * 100));
    onSliderChange(percentage);
  };

  const handleTouchEnd = () => {
    if (!isDraggingRef.current) return;

    isDraggingRef.current = false;

    // Re-enable map dragging
    if (map) {
      map.dragging.enable();
    }
  };

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('touchmove', handleTouchMove);
    document.addEventListener('touchend', handleTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [enabled, map]);

  if (!enabled) return null;

  return (
    <div
      ref={sliderRef}
      className="layer-swipe-slider"
      style={{
        left: `${sliderLeftPx}px` // Use pixel position to match clip exactly
      }}
      onMouseDown={handleMouseDown}
      onTouchStart={handleTouchStart}
    >
      <div className="slider-handle">
        <div className="slider-line" />
        <div className="slider-grip">
          <span>◀</span>
          <span>▶</span>
        </div>
        <div className="slider-line" />
      </div>
    </div>
  );
};

export default LayerSwipe;
